import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ChooseReportingType from "../components/chooseReportingType";
import ContainerHeader from "../components/containerHeader";
import {
  CreateReportPayload,
  ReportStatus,
  ReportType,
} from "../api/models/report.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useNavigate } from "react-router";
import useReports from "../hooks/useReports";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import { useReportingCycleContext } from "../contexts/reportingCycleContext";
import useReport from "../hooks/useReport";
import ReportChoiceDialog from "../dialogs/reportChoiceDialog";
import { Report } from "../api/models/report.models";

const Wrapper = styled.div`
  position: relative;
  min-height: calc(100vh - var(--dynamic-footer-height));
`;

const PaddedContainer = styled.div`
  padding: 6rem 3rem;
  text-align: left;
  min-height: calc(100vh - var(--dynamic-footer-height));
`;

const UnsentNotification = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
  max-width: 700px;
  margin-bottom: 2rem;

  & a {
    color: var(--sumi-blue);
    text-decoration: underline;
  }
`;

const Header = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 2rem;
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;

  > div {
    margin: auto 0;
  }
`;

const ReportingChoice: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showReportTypeDialog, setShowReportTypeDialog] = React.useState(false);

  const [
    toast,
    companyId,
    organizationId,
    year,
    quarter,
    month,
    setYear,
    setQuarter,
    setMonth,
    organizationName,
    companyName,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.toastRef,
      state.companyId,
      state.organizationId,
      state.selectedYearForNewReport,
      state.selectedQuarterForNewReport,
      state.selectedMonthForNewReport,
      state.setSelectedYearForNewReport,
      state.setSelectedQuarterForNewReport,
      state.setSelectedMonthForNewReport,
      state.organizationName,
      state.companyName,
    ])
  );

  const { createNewReport, isCreatingNewReport } = useReport({
    reportId: undefined,
    disableSearch: true,
  });

  const { reportingCycle } = useReportingCycleContext();

  const { reportsData, isLoadingReports } = useReports({
    companyId,
    organizationId,
  });

  const checkIfReportExists = (reportType: ReportType) => {
    if (!reportsData || !year) return false;

    // When in quarterly mode, quarter must be selected
    if (reportingCycle === ReportingCycleEnum.QUARTERLY && !quarter) {
      return false;
    }

    // When in monthly mode, month must be selected
    if (reportingCycle === ReportingCycleEnum.MONTHLY && !month) {
      return false;
    }

    return reportsData.some((report) => {
      const matchesType = report.reportType === reportType;
      const matchesYear = report.year === year;
      const matchesQuarter =
        reportingCycle === ReportingCycleEnum.QUARTERLY
          ? report.quarter === quarter
          : true;
      const matchesMonth =
        reportingCycle === ReportingCycleEnum.MONTHLY
          ? report.month === month
          : true;

      return matchesYear && matchesQuarter && matchesMonth && matchesType;
    });
  };

  const handleReportTypeSelect = async (type: ReportType) => {
    // Check if report already exists
    if (checkIfReportExists(type)) {
      toast?.current?.show({
        severity: "error",
        summary: t("error-report-already-exists"),
        detail: t("error-report-already-exists-detail"),
        life: 5000,
      });
      return;
    }

    if (type === ReportType.EAN) {
      navigate(`/new-report`);
    }

    if (type === ReportType.DETAILED) {
      /*  const result = await handleReportCreate(ReportType.DETAILED);
      navigate(`/detailed-report/${result?.id}`); */
      setShowReportTypeDialog(true);
    }
  };

  const handleReportCreate = async (type: ReportType) => {
    if (!year) {
      toast?.current?.show({
        severity: "error",
        detail: t("error-no-year-selected-detail"),
        life: 5000,
      });
      return;
    }

    const createReportPayload: CreateReportPayload = {
      companyId: companyId as number,
      organizationId: organizationId ? organizationId : undefined,
      year: year ? year : 2025,
      quarter:
        reportingCycle === ReportingCycleEnum.QUARTERLY
          ? quarter
            ? quarter
            : 1
          : undefined,
      month:
        reportingCycle === ReportingCycleEnum.MONTHLY
          ? month
            ? month
            : 1
          : undefined,
      reportStatus: ReportStatus.Draft,
      rows: [],
      reportType: type,
    };
    const result = await createNewReport(createReportPayload, undefined);

    if (result) {
      // reset the selected year, quarter, month in the store
      setYear(null);
      setQuarter(null);
      setMonth(null);
    }

    return result;
  };

  const getReportLink = (report?: Report) => {
    if (!report) return "#";

    switch (report.reportType) {
      case ReportType.EAN:
        return `/report/${report.id}`;
      case ReportType.LIGHT:
        return `/light-report/${report.id}`;
      case ReportType.DETAILED:
        return `/detailed-report/${report.id}`;
      default:
        return "#";
    }
  };

  const getNotification = () => {
    if (reportsData && reportsData.length > 0) {
      const sortedData = [...reportsData];
      sortedData.sort(
        (a, b) =>
          new Date(b.created as string).getTime() -
          new Date(a.created as string).getTime()
      );
      const currentYear = new Date().getFullYear();
      const unsentReport = sortedData.find(
        (report) =>
          report.reportStatus === ReportStatus.Draft &&
          new Date(report.created as string).getFullYear() === currentYear
      );

      if (unsentReport) {
        return (
          <UnsentNotification>
            {t("notification-unsent-report")}{" "}
            <a href={getReportLink(unsentReport)}>
              {t("notification-view-report")}
            </a>
          </UnsentNotification>
        );
      }
    }
    return null;
  };

  const getContent = () => {
    return (
      <ChooseReportingType
        handleSelect={handleReportTypeSelect}
        isCreatingNewReport={false}
        eanReportAlreadyExists={checkIfReportExists(ReportType.EAN)}
        lightOrDetailedReportAlreadyExists={
          checkIfReportExists(ReportType.LIGHT) ||
          checkIfReportExists(ReportType.DETAILED)
        }
      />
    );
  };

  const getDescription = () => {
    return t("subheader-choose-report-type");
  };

  return (
    <Wrapper>
      <ContainerHeader
        title={t("header-choose-report-type")}
        description={getDescription()}
      />
      <PaddedContainer>
        <ReportChoiceDialog
          visible={showReportTypeDialog}
          onHide={() => setShowReportTypeDialog(false)}
          onSelect={async (reportType) => {
            if (reportType === ReportType.LIGHT) {
              const result = await handleReportCreate(ReportType.LIGHT);
              if (result) {
                navigate(`/light-report/${result.id}`);
              }
              return;
            }

            if (reportType === ReportType.DETAILED) {
              const result = await handleReportCreate(ReportType.DETAILED);
              if (result) {
                navigate(`/detailed-report/${result.id}`);
              }
            }
          }}
        />
        <LoadingContainer
          status={
            isLoadingReports
              ? HttpRequestStatus.LOADING
              : HttpRequestStatus.SUCCESS
          }
        >
          <Header>
            {organizationId && organizationName ? (
              <span>
                {t("text-creating-report-for-organization", {
                  organization: organizationName,
                })}
              </span>
            ) : (
              <span>
                {t("text-creating-report-for-company", {
                  company: companyName,
                })}
              </span>
            )}
          </Header>
          {getNotification()}
          {getContent()}
        </LoadingContainer>
      </PaddedContainer>
    </Wrapper>
  );
};

export default ReportingChoice;
